import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  FileText,
  Shield,
  FileSignature,
  Home,
  Cookie,
  RotateCcw,
  Truck,
  Copyright,
  Users,
  Gavel,
  Database,
  Settings,
  UserX,
  ShieldCheck,
  XCircle,
  CreditCard,
  MessageSquare,
  AlertTriangle,
  Scale,
  Store,
  Ban,
  DollarSign,
} from "lucide-react";
import clsx from "clsx";

interface LayoutProps {
  children: React.ReactNode;
}

const navigationItems = [
  { path: "/", label: "Home", icon: Home },
  { path: "/documents", label: "All Documents", icon: FileText },
  { path: "/terms", label: "Terms & Conditions", icon: FileText },
  { path: "/privacy", label: "Privacy Policy", icon: Shield },
  {
    path: "/account-suspension-policy",
    label: "Account Suspension Policy",
    icon: UserX,
  },
  {
    path: "/buyer-protection-policy",
    label: "Buyer Protection Policy",
    icon: ShieldCheck,
  },
  { path: "/cancellation-policy", label: "Cancellation Policy", icon: XCircle },
  {
    path: "/commission-fee-structure",
    label: "Commission & Fee Structure",
    icon: DollarSign,
  },
  {
    path: "/content-review-policy",
    label: "Content & Review Policy",
    icon: MessageSquare,
  },
  {
    path: "/dispute-resolution-policy",
    label: "Dispute Resolution Policy",
    icon: Gavel,
  },
  {
    path: "/intellectual-property-policy",
    label: "Intellectual Property Policy",
    icon: Copyright,
  },
  {
    path: "/legal-compliance-policy",
    label: "Legal Compliance Policy",
    icon: Scale,
  },
  { path: "/payment-policy", label: "Payment Policy", icon: CreditCard },
  {
    path: "/prohibited-items-policy",
    label: "Prohibited Items Policy",
    icon: Ban,
  },
  { path: "/refund-policy", label: "Return & Refund Policy", icon: RotateCcw },
  { path: "/seller-policy", label: "Seller Policy", icon: Store },
  {
    path: "/shipping-policy",
    label: "Shipping & Delivery Policy",
    icon: Truck,
  },
  { path: "/agreements", label: "Agreements", icon: FileSignature },
  { path: "/cookie-policy", label: "Cookie Policy", icon: Cookie },
  { path: "/code-of-conduct", label: "Code of Conduct", icon: Users },
  {
    path: "/data-processing-agreement",
    label: "Data Processing Agreement",
    icon: Database,
  },
  {
    path: "/service-level-agreement",
    label: "Service Level Agreement",
    icon: Settings,
  },
  {
    path: "/delete-account",
    label: "Delete Account Policy",
    icon: UserX,
  },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-2">
            <img
              src="/logo.png"
              alt="Baakas"
              className="w-8 h-8 object-contain"
            />
            <span className="font-semibold text-gray-900">Baakas Legal</span>
          </div>
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <div
        className={clsx(
          "lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block lg:w-64 lg:bg-white lg:shadow-lg lg:border-r lg:border-gray-200",
          sidebarOpen
            ? "fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg border-r border-gray-200"
            : "hidden"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center px-4 lg:px-6 py-4 border-b border-gray-200">
            <img
              src="/logo.png"
              alt="Baakas"
              className="w-8 h-8 lg:w-10 lg:h-10 object-contain"
            />
            <div className="ml-3">
              <h1 className="text-base lg:text-lg font-semibold text-gray-900">
                Baakas Legal
              </h1>
              <p className="text-xs lg:text-sm text-gray-500">
                Legal Documents
              </p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 lg:px-4 py-4 lg:py-6 space-y-1 lg:space-y-2 sidebar-nav overflow-y-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={clsx(
                    "flex items-center px-2 lg:px-3 py-2 text-xs lg:text-sm font-medium rounded-lg transition-colors duration-200",
                    isActive
                      ? "bg-primary-50 text-primary-600 border-r-2 border-primary-600"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  )}
                >
                  <Icon size={18} className="mr-2 lg:mr-3 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="px-3 lg:px-4 py-3 lg:py-4 border-t border-gray-200">
            <div className="text-xs text-gray-500 text-center">
              <p>© 2024 Baakas</p>
              <p className="hidden lg:block">All rights reserved</p>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="lg:ml-64">
        <main className="min-h-screen">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
